import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import accessCardImg from "./level2/access.png";
import {
  STAGE1_DESK_ITEMS,
  STAGE1_DESK_SVG_DRAG_RULES,
  STAGE1_DESK_TABLE_DESKTOP,
  STAGE1_DESK_TABLE_MOBILE,
  STAGE1_DESK_MOBILE_VIEWBOX,
  STAGE1_DESK_TARGETS,
  getDeskItemById,
  getDeskSvgDragRuleBySvgId,
  isDeskSvgDragItem,
  validateDeskOrganize,
} from "./stage1DeskOrganizeData";

function isMobDeskViewport() {
  const w = window.innerWidth;
  const narrow = w > 0 && w <= 900;
  const portrait =
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  return narrow && portrait;
}

function parseSvgTransform(target) {
  let currentX = 0;
  let currentY = 0;
  let scale = 1;
  const transform = target.getAttribute("transform");

  if (transform) {
    const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
    if (matrixMatch?.[1]) {
      const matrix = matrixMatch[1].split(/\s+/).map(Number);
      currentX = matrix[4];
      currentY = matrix[5];
      scale = matrix[0];
    } else {
      const translateMatch = transform.match(/translate\(([^)]+)\)/);
      if (translateMatch?.[1]) {
        const coords = translateMatch[1].split(/[\s,]+/);
        currentX = parseFloat(coords[0]) || 0;
        currentY = parseFloat(coords[1]) || 0;
      }
      const scaleMatch = transform.match(/scale\(([^)]+)\)/);
      if (scaleMatch?.[1]) {
        scale = parseFloat(scaleMatch[1]) || 1;
      }
    }
  }

  if (currentX === 0 && currentY === 0 && typeof target.getBBox === "function") {
    try {
      const bbox = target.getBBox();
      currentX = bbox.x;
      currentY = bbox.y;
    } catch {
      // Element may not be measurable yet.
    }
  }

  return { currentX, currentY, scale };
}

function clientToSvgPoint(svg, clientX, clientY) {
  const point = svg.createSVGPoint();
  point.x = clientX;
  point.y = clientY;
  return point.matrixTransform(svg.getScreenCTM().inverse());
}

function showSvgElement(el) {
  if (!el) return;
  el.style.setProperty("display", "block", "important");
  el.style.setProperty("visibility", "visible", "important");
  el.style.setProperty("opacity", "1", "important");
}

function hideSvgElement(el) {
  if (!el) return;
  el.style.setProperty("display", "none", "important");
}

function clearSvgElementVisibility(el) {
  if (!el) return;
  el.style.removeProperty("display");
  el.style.removeProperty("visibility");
  el.style.removeProperty("opacity");
}

function isPointInRect(clientX, clientY, rect, padding = 20) {
  return (
    clientX >= rect.left - padding &&
    clientX <= rect.right + padding &&
    clientY >= rect.top - padding &&
    clientY <= rect.bottom + padding
  );
}

function rectOverlapRatio(dragRect, zoneRect) {
  const overlapLeft = Math.max(dragRect.left, zoneRect.left);
  const overlapRight = Math.min(dragRect.right, zoneRect.right);
  const overlapTop = Math.max(dragRect.top, zoneRect.top);
  const overlapBottom = Math.min(dragRect.bottom, zoneRect.bottom);
  if (overlapLeft >= overlapRight || overlapTop >= overlapBottom) return 0;
  const overlapArea = (overlapRight - overlapLeft) * (overlapBottom - overlapTop);
  const dragArea = Math.max(dragRect.width * dragRect.height, 1);
  return overlapArea / dragArea;
}

function getDropHitPadding(rule, isMobile) {
  if (isMobile && rule.dropHitPaddingMob != null) return rule.dropHitPaddingMob;
  if (rule.dropHitPadding != null) return rule.dropHitPadding;
  return isMobile ? 32 : 20;
}

function isDropZoneHit(dragElement, clientX, clientY, zoneRect, padding) {
  const dragRect = dragElement.getBoundingClientRect();
  const centerX = dragRect.left + dragRect.width / 2;
  const centerY = dragRect.top + dragRect.height / 2;
  return (
    isPointInRect(clientX, clientY, zoneRect, padding) ||
    isPointInRect(centerX, centerY, zoneRect, padding) ||
    rectOverlapRatio(dragRect, zoneRect) >= 0.12
  );
}

function findDropZoneForDrag(svg, dragElement, clientX, clientY, activeRule, isMobile) {
  if (activeRule) {
    const intendedZone = svg.getElementById(activeRule.dropZoneId);
    if (intendedZone) {
      const zoneRect = intendedZone.getBoundingClientRect();
      const padding = getDropHitPadding(activeRule, isMobile);
      if (isDropZoneHit(dragElement, clientX, clientY, zoneRect, padding)) {
        return activeRule;
      }
    }
  }

  const dragRect = dragElement.getBoundingClientRect();
  let bestRule = null;
  let bestScore = 0;

  for (const rule of STAGE1_DESK_SVG_DRAG_RULES) {
    const zone = svg.getElementById(rule.dropZoneId);
    if (!zone) continue;

    const zoneRect = zone.getBoundingClientRect();
    const padding = getDropHitPadding(rule, isMobile);
    const overlap = rectOverlapRatio(dragRect, zoneRect);
    const pointerHit = isPointInRect(clientX, clientY, zoneRect, padding);
    const centerX = dragRect.left + dragRect.width / 2;
    const centerY = dragRect.top + dragRect.height / 2;
    const centerHit = isPointInRect(centerX, centerY, zoneRect, padding);
    const score = Math.max(overlap, pointerHit ? 0.55 : 0, centerHit ? 0.45 : 0);

    if (score > bestScore && (pointerHit || centerHit || overlap >= 0.15)) {
      bestScore = score;
      bestRule = rule;
    }
  }

  return bestRule;
}

function syncSvgDeskVisibility(svg, placedIdsSet) {
  STAGE1_DESK_SVG_DRAG_RULES.forEach((rule) => {
    const placed = placedIdsSet.has(rule.itemId);
    const dragEl = svg.getElementById(rule.svgId);
    if (dragEl) {
      if (placed) hideSvgElement(dragEl);
      else clearSvgElementVisibility(dragEl);
    }

    rule.showIds.forEach((svgId) => {
      const el = svg.getElementById(svgId);
      if (!el) return;
      if (placed) showSvgElement(el);
      else hideSvgElement(el);
    });

    (rule.alsoShowIds || []).forEach((svgId) => {
      const el = svg.getElementById(svgId);
      if (!el) return;
      if (placed) showSvgElement(el);
      else clearSvgElementVisibility(el);
    });

    rule.hideIds.forEach((svgId) => {
      const el = svg.getElementById(svgId);
      if (!el) return;
      if (placed) hideSvgElement(el);
      else clearSvgElementVisibility(el);
    });
  });
}

function applyRuleSuccessVisuals(svg, rule) {
  const dragEl = svg.getElementById(rule.svgId);
  if (dragEl) hideSvgElement(dragEl);

  rule.showIds.forEach((svgId) => {
    showSvgElement(svg.getElementById(svgId));
  });

  (rule.alsoShowIds || []).forEach((svgId) => {
    showSvgElement(svg.getElementById(svgId));
  });

  rule.hideIds.forEach((svgId) => {
    hideSvgElement(svg.getElementById(svgId));
  });
}

function SortListItem({ item, placed }) {
  return (
    <li
      className={`stage1-desk-sort__item${
        placed ? " stage1-desk-sort__item--done" : ""
      }`}
    >
      <img src={item.icon} alt="" className="stage1-desk-sort__icon" draggable={false} />
      <span className="stage1-desk-sort__label">{item.label}</span>
      <span
        className="stage1-desk-sort__dot"
        style={{ backgroundColor: placed ? "#22c55e" : item.dotColor }}
        aria-hidden
      />
    </li>
  );
}

function DeskDraggable({ item, onDragStart, hidden, isMobile }) {
  if (hidden) return null;
  const pos = isMobile && item.deskPosMob ? item.deskPosMob : item.deskPos;
  return (
    <button
      type="button"
      className={`stage1-desk-item stage1-desk-item--${item.deskSize || "md"}`}
      style={{ left: pos.left, top: pos.top }}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/desk-item-id", item.id);
        e.dataTransfer.effectAllowed = "move";
        onDragStart(item.id);
      }}
      aria-label={`Drag ${item.label} to the correct target area`}
    />
  );
}

function SvgDropZone({ target, isActive, isFilled, isMobile, onDrop, onDragOver }) {
  const pos = isMobile && target.dropPosMob ? target.dropPosMob : target.dropPos;
  return (
    <div
      className={`stage1-desk-dropzone stage1-desk-dropzone--${
        target.dropSize || "md"
      }${isActive ? " stage1-desk-dropzone--active" : ""}${
        isFilled ? " stage1-desk-dropzone--filled" : ""
      }`}
      style={{ left: pos.left, top: pos.top }}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(target.id, e)}
      role="button"
      tabIndex={-1}
      aria-label={target.label}
    />
  );
}

function getHtmlDropTargets() {
  return STAGE1_DESK_TARGETS.filter((target) => target.id === "drawer");
}

export default function Stage1DeskOrganize({ onComplete, submitting = false }) {
  const [placements, setPlacements] = useState({});
  const [draggingId, setDraggingId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [showAccessCard, setShowAccessCard] = useState(false);
  const [cardRevealed, setCardRevealed] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobDeskViewport);
  const [svgReady, setSvgReady] = useState(false);

  const svgHostRef = useRef(null);
  const draggedItemRef = useRef(null);
  const placedIdsRef = useRef(new Set());
  const placeItemRef = useRef(null);
  const setFeedbackRef = useRef(setFeedback);
  const setDraggingIdRef = useRef(setDraggingId);
  const onCompleteRef = useRef(onComplete);
  const submittingRef = useRef(submitting);
  const puzzleFinishedRef = useRef(false);
  const placementsRef = useRef({});
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    setFeedbackRef.current = setFeedback;
    setDraggingIdRef.current = setDraggingId;
    onCompleteRef.current = onComplete;
    submittingRef.current = submitting;
  }, [onComplete, submitting]);

  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    const sync = () => setIsMobile(isMobDeskViewport());
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("orientationchange", sync);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("orientationchange", sync);
    };
  }, []);

  const placedIds = useMemo(() => new Set(Object.keys(placements)), [placements]);
  const filledTargets = useMemo(() => new Set(Object.values(placements)), [placements]);
  const htmlDropTargets = useMemo(() => getHtmlDropTargets(), []);

  useEffect(() => {
    placedIdsRef.current = placedIds;
  }, [placedIds]);

  const checkPuzzleComplete = useCallback((nextPlacements) => {
    const result = validateDeskOrganize(nextPlacements);
    if (!result.allPlaced || !result.valid || puzzleFinishedRef.current) return;

    puzzleFinishedRef.current = true;
    placementsRef.current = nextPlacements;
    setShowAccessCard(true);
    window.setTimeout(() => setCardRevealed(true), 600);
  }, []);

  const handleAccessCardClick = useCallback(() => {
    if (submittingRef.current || !puzzleFinishedRef.current) return;
    onCompleteRef.current?.(placementsRef.current);
  }, []);

  const placeItem = useCallback(
    (itemId, targetId, alsoPlaceItemIds = []) => {
      setPlacements((prev) => {
        const next = { ...prev, [itemId]: targetId };
        alsoPlaceItemIds.forEach((extraId) => {
          const extra = getDeskItemById(extraId);
          if (extra) next[extraId] = extra.correctTarget;
        });
        checkPuzzleComplete(next);
        return next;
      });
      setDraggingId(null);
      setFeedback(null);
    },
    [checkPuzzleComplete]
  );

  useEffect(() => {
    placeItemRef.current = placeItem;
  }, [placeItem]);

  const handleDragMove = useCallback((e) => {
    const dragState = draggedItemRef.current;
    if (!dragState) return;

    e.preventDefault();
    const { element, offsetX, offsetY, scale } = dragState;
    const svg = element.ownerSVGElement;
    if (!svg) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const svgPoint = clientToSvgPoint(svg, clientX, clientY);
    const newX = svgPoint.x - offsetX;
    const newY = svgPoint.y - offsetY;

    element.setAttribute("transform", `matrix(${scale} 0 0 ${scale} ${newX} ${newY})`);
  }, []);

  const handleDragEnd = useCallback(
    (e) => {
      const dragState = draggedItemRef.current;
      if (!dragState) return;

      const target = dragState.element;
      const svg = target.ownerSVGElement;
      const rule = getDeskSvgDragRuleBySvgId(target.id);
      let shouldRevert = true;

      const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
      const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;

      if (svg && rule) {
        const hitRule = findDropZoneForDrag(
          svg,
          target,
          clientX,
          clientY,
          rule,
          isMobileRef.current
        );

        if (hitRule && hitRule.itemId === rule.itemId) {
          applyRuleSuccessVisuals(svg, hitRule);
          const placedNow = [hitRule.itemId, ...(hitRule.alsoPlaceItemIds || [])];
          placedIdsRef.current = new Set([...placedIdsRef.current, ...placedNow]);
          placeItemRef.current?.(
            hitRule.itemId,
            hitRule.correctTarget,
            hitRule.alsoPlaceItemIds || []
          );
          shouldRevert = false;
        } else if (hitRule) {
          const item = getDeskItemById(rule.itemId);
          setFeedbackRef.current({
            type: "error",
            message: `“${item?.label || "That item"}” doesn’t belong there. Try another spot.`,
          });
        } else {
          setFeedbackRef.current({
            type: "error",
            message: "That doesn’t belong there. Try another spot.",
          });
        }
      }

      if (shouldRevert) {
        const { originalX, originalY, scale } = dragState;
        target.style.transition = "transform 0.3s ease";
        target.setAttribute(
          "transform",
          `matrix(${scale} 0 0 ${scale} ${originalX} ${originalY})`
        );
        window.setTimeout(() => {
          target.style.transition = "none";
        }, 300);
      }

      target.style.cursor = "grab";
      target.style.pointerEvents = "auto";
      draggedItemRef.current = null;
      setDraggingIdRef.current(null);

      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchend", handleDragEnd);
    },
    [handleDragMove]
  );

  const handleDragStart = useCallback(
    (e) => {
      if (puzzleFinishedRef.current) return;
      const target = e.target;
      if (!target.classList?.contains("stage1-desk-draggable")) return;

      const rule = getDeskSvgDragRuleBySvgId(target.id);
      if (!rule || placedIdsRef.current.has(rule.itemId)) return;

      e.preventDefault();
      target.style.cursor = "grabbing";
      target.style.pointerEvents = "none";
      if (target.parentNode) {
        target.parentNode.appendChild(target);
      }

      const { currentX, currentY, scale } = parseSvgTransform(target);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const svg = target.ownerSVGElement;
      const svgPoint = svg ? clientToSvgPoint(svg, clientX, clientY) : { x: 0, y: 0 };

      draggedItemRef.current = {
        element: target,
        originalX: currentX,
        originalY: currentY,
        scale,
        offsetX: svgPoint.x - currentX,
        offsetY: svgPoint.y - currentY,
      };

      setDraggingIdRef.current(rule.itemId);

      if (e.touches) {
        document.addEventListener("touchmove", handleDragMove, { passive: false });
        document.addEventListener("touchend", handleDragEnd, { once: true });
      } else {
        document.addEventListener("mousemove", handleDragMove);
        document.addEventListener("mouseup", handleDragEnd, { once: true });
      }
    },
    [handleDragEnd, handleDragMove]
  );

  useEffect(() => {
    const host = svgHostRef.current;
    if (!host) return undefined;

    let disposed = false;
    let container = null;
    const draggableListeners = [];

    const onDraggableStart = (event) => {
      event.stopPropagation();
      handleDragStart(event);
    };

    const onContainerMouseDown = (event) => {
      if (event.target.classList?.contains("stage1-desk-draggable")) {
        onDraggableStart(event);
      }
    };

    const bindDraggableElements = (svg) => {
      STAGE1_DESK_SVG_DRAG_RULES.forEach((rule) => {
        const el = svg.getElementById(rule.svgId);
        if (!el) return;
        el.classList.add("stage1-desk-draggable");
        el.style.cursor = "grab";
        el.style.transition = "none";
        el.addEventListener("mousedown", onDraggableStart);
        el.addEventListener("touchstart", onDraggableStart, { passive: false });
        draggableListeners.push({ el, handler: onDraggableStart });
      });
    };

    const unbindDraggableElements = () => {
      draggableListeners.forEach(({ el, handler }) => {
        el.removeEventListener("mousedown", handler);
        el.removeEventListener("touchstart", handler);
      });
      draggableListeners.length = 0;
    };

    const loadDeskSvg = async () => {
      try {
        const svgUrl = isMobile ? STAGE1_DESK_TABLE_MOBILE : STAGE1_DESK_TABLE_DESKTOP;
        const viewBox = isMobile ? STAGE1_DESK_MOBILE_VIEWBOX : "0 0 1920 1080";
        const response = await fetch(svgUrl);
        const svgText = await response.text();
        if (disposed || !svgHostRef.current) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(svgText, "image/svg+xml");
        const svg = doc.querySelector("svg");
        if (!svg) return;

        svg.classList.add("stage1-desk-svg");
        svg.setAttribute("viewBox", viewBox);

        bindDraggableElements(svg);

        syncSvgDeskVisibility(svg, placedIdsRef.current);

        container = document.createElement("div");
        container.className = "stage1-desk-svg-content";
        container.appendChild(svg);
        host.innerHTML = "";
        host.appendChild(container);

        container.addEventListener("mousedown", onContainerMouseDown);

        if (!disposed) setSvgReady(true);
      } catch (error) {
        console.error("Failed to load desk SVG:", error);
      }
    };

    setSvgReady(false);
    loadDeskSvg();

    return () => {
      disposed = true;
      setSvgReady(false);
      unbindDraggableElements();
      if (container) {
        container.removeEventListener("mousedown", onContainerMouseDown);
      }
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("touchmove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchend", handleDragEnd);
      draggedItemRef.current = null;
    };
  }, [isMobile, handleDragStart, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (!svgReady) return;
    const svg = svgHostRef.current?.querySelector("svg.stage1-desk-svg");
    if (!svg) return;
    syncSvgDeskVisibility(svg, placedIds);
  }, [placedIds, svgReady]);

  const allowDrop = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDropOnTarget = useCallback(
    (targetId, e) => {
      if (puzzleFinishedRef.current) return;
      e.preventDefault();
      const itemId = e.dataTransfer.getData("text/desk-item-id");
      if (!itemId) return;

      const item = getDeskItemById(itemId);
      if (!item) return;

      if (item.correctTarget !== targetId) {
        setFeedback({
          type: "error",
          message: `“${item.label}” doesn’t belong there. Try another spot.`,
        });
        setDraggingId(null);
        return;
      }

      placeItem(itemId, targetId);
    },
    [placeItem]
  );

  return (
    <div
      className={`stage1-desk-organize${
        isMobile ? " stage1-desk-organize--mob" : ""
      }`}
    >
      <header className="stage1-desk-organize__header">
        <h2 className="stage1-desk-organize__title">Organize Your Desk</h2>
        <p className="stage1-desk-organize__subtitle">
          Drag misplaced items to their proper homes
        </p>
      </header>

      <div className="stage1-desk-organize__layout">
        <aside className="stage1-desk-sort">
          <h3 className="stage1-desk-sort__heading">Items to Sort</h3>
          <ul className="stage1-desk-sort__list">
            {STAGE1_DESK_ITEMS.map((item) => (
              <SortListItem key={item.id} item={item} placed={placedIds.has(item.id)} />
            ))}
          </ul>
        </aside>

        <div className="stage1-desk-scene-wrap">
          <div
            className={`stage1-desk-table-stage${
              isMobile ? " stage1-desk-table-stage--mob" : ""
            }${draggingId ? " stage1-desk-table-stage--dragging" : ""}`}
          >
            <div
              ref={svgHostRef}
              className="stage1-desk-table-stage__table stage1-desk-svg-host"
              aria-label="Desk organization puzzle"
            />
            <div className="stage1-desk-table-stage__surface" aria-hidden={false}>
              {STAGE1_DESK_ITEMS.map((item) => (
                <DeskDraggable
                  key={item.id}
                  item={item}
                  hidden={
                    placedIds.has(item.id) || isDeskSvgDragItem(item.id)
                  }
                  isMobile={isMobile}
                  onDragStart={setDraggingId}
                />
              ))}
            </div>
            {htmlDropTargets.map((target) => (
              <SvgDropZone
                key={target.id}
                target={target}
                isActive={Boolean(draggingId)}
                isFilled={filledTargets.has(target.id)}
                isMobile={isMobile}
                onDrop={handleDropOnTarget}
                onDragOver={allowDrop}
              />
            ))}
            {showAccessCard ? (
              <button
                type="button"
                className={`stage1-desk-access-card${
                  cardRevealed ? " stage1-desk-access-card--revealed" : ""
                }`}
                onClick={handleAccessCardClick}
                disabled={submitting || !cardRevealed}
                aria-label="Meeting Room Access Card — tap to continue"
              >
                <img
                  src={accessCardImg}
                  alt="Meeting Room Access Card"
                  className="stage1-desk-access-card__img"
                  draggable={false}
                />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {feedback ? (
        <p
          className={`stage1-desk-organize__feedback stage1-desk-organize__feedback--${feedback.type}`}
          role="alert"
        >
          {feedback.message}
        </p>
      ) : showAccessCard && cardRevealed ? (
        <p className="stage1-desk-organize__feedback stage1-desk-organize__feedback--success">
          Desk cleared! Tap the access card to continue.
        </p>
      ) : null}
    </div>
  );
}
